import React, { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { RoutePath } from "../../../routes/route-path";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { UserResetPassword } from "api/user";
import LoadingComponent from "common/components/loader";

const ResetPasswordComponent: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Retrieve query parameters
  const username = searchParams.get("username");
  const code = searchParams.get("code");

  const formik = useFormik({
    initialValues: {
      username: username ?? "",
      code: code ?? "",
      password: "",
      passwordConfirm: "",
    },
    validationSchema: Yup.object({
      //email: Yup.string().email("Invalid email address").required("Email is required"),
      username: Yup.string().required("Username is required"),
      code: Yup.string().required("Code is required"),
      password: Yup.string().required("Password is required").min(6, "Password must be at least 6 characters"),
      passwordConfirm: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: async (values) => {
      // Handle form submission logic here (e.g., send reset link)

      try {
        setError("");
        setSuccess("");
        setLoading(true);
        const res = await UserResetPassword({
          username: values.username ?? "",
          code: values.code ?? "",
          newPassword: values.password ?? "",
        });

        if (!(res?.status?.isError ?? false) && (res?.data?.status ?? false)) {
          setSuccess("success");
        } else {
          setError("error");
        }
        setLoading(false);
      } catch (error) {
        setError("error");
        setLoading(false);
      }
    },
  });

  return (
    <>
      <div className="mt-8 text-center">
        <h4 className="mb-2 text-custom-500 dark:text-custom-500">Set a New Password</h4>
        <p className="mb-8 text-slate-500 dark:text-zink-200">
          Your new password should be distinct from any of your prior passwords
        </p>
      </div>

      <form autoComplete="off" onSubmit={formik.handleSubmit}>
        <div className="mb-3">
          <label htmlFor="usernameInput" className="inline-block mb-2 text-base font-medium">
            Username
          </label>
          <input
            type="text"
            className={`form-input
            focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 
            disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 
            disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 
            placeholder:text-slate-400 dark:placeholder:text-zink-200
             ${
               formik.touched.username && formik.errors.username
                 ? "border-red-500"
                 : "border-slate-200 dark:border-zink-500"
             }`}
            required
            disabled
            placeholder="Username"
            id="usernameInput"
            name="username"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.username}
          />
          {formik.touched.username && formik.errors.username ? (
            <div className="text-sm text-red-500">{formik.errors.username}</div>
          ) : null}
        </div>

        <div className="mb-3">
          <label htmlFor="codeInput" className="inline-block mb-2 text-base font-medium">
            Code
          </label>
          <input
            type="text"
            className={`form-input 
              focus:outline-none 
              focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 
              dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 
              dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 
              dark:placeholder:text-zink-200 ${
                formik.touched.code && formik.errors.code ? "border-red-500" : "border-slate-200 dark:border-zink-500"
              }`}
            required
            disabled
            placeholder="Code"
            id="codeInput"
            name="code"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.code}
          />
          {formik.touched.code && formik.errors.code ? (
            <div className="text-sm text-red-500">{formik.errors.code}</div>
          ) : null}
        </div>

        <div className="mb-3">
          <label htmlFor="passwordInput" className="inline-block mb-2 text-base font-medium">
            Password
          </label>
          <input
            type="password"
            className={`form-input 
               focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 
               disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 
               disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 
               placeholder:text-slate-400 dark:placeholder:text-zink-200
               ${
                 formik.touched.password && formik.errors.password
                   ? "border-red-500"
                   : "border-slate-200 dark:border-zink-500"
               }`}
            required
            placeholder="Password"
            id="passwordInput"
            name="password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
          />
          {formik.touched.password && formik.errors.password ? (
            <div className="text-sm text-red-500">{formik.errors.password}</div>
          ) : null}
        </div>
        <div className="mb-3">
          <label htmlFor="passwordConfirmInput" className="inline-block mb-2 text-base font-medium">
            Confirm Password
          </label>
          <input
            type="password"
            className={`form-input  focus:outline-none focus:border-custom-500 disabled:bg-slate-100 
            dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 
            dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 
            dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200
            ${
              formik.touched.passwordConfirm && formik.errors.passwordConfirm
                ? "border-red-500"
                : "border-slate-200 dark:border-zink-500"
            }`}
            required
            placeholder="Confirm password"
            id="passwordConfirmInput"
            name="passwordConfirm"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.passwordConfirm}
          />
          {formik.touched.passwordConfirm && formik.errors.passwordConfirm ? (
            <div className="text-sm text-red-500">{formik.errors.passwordConfirm}</div>
          ) : null}
        </div>

        <div className="mt-8">
          <button
            type="submit"
            className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
          >
            Reset Password
          </button>
        </div>
        <div className="mt-4 text-center">
          <p className="mb-0">
            Hold on, I've got my password...{" "}
            <Link to={RoutePath.Admin_Login_page} className="underline fw-medium text-custom-500">
              {" "}
              Click here{" "}
            </Link>{" "}
          </p>
        </div>
      </form>

      {success && (
        <div
          className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
          id="successAlert"
        >
          <b>Success! </b> Your password has been changed now.
        </div>
      )}
      {error && (
        <div
          className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
          id="successAlert"
        >
          Oops! Something went wrong while attempting to reset password reset. Please try again later or contact support
          for assistance.
        </div>
      )}
      {loading && <LoadingComponent></LoadingComponent>}
    </>
  );
};
export default ResetPasswordComponent;
